import { HeadersFunction, LoaderFunctionArgs } from '@remix-run/node';
import { Link, NavLink, useLoaderData } from '@remix-run/react';
import {
	GQLJournalArticleStatusType,
	GQLJournalFragment,
	GetContentPageDocument,
	GetJournalArticlesDocument,
	GetJournalCategoriesDocument,
} from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonDay } from '@theprintbar/server';
import { WebRoutes, cn, ensuredSearchObject, getSearchObject, invariantNotFound } from '@theprintbar/shared';
import { ButtonSize, CTAText, ImageSplitCTA, OutlineLinkButton, Pill, RoundedCTA, SmallArrowIcon, SplitCTA } from '@theprintbar/ui';
import { z } from 'zod';
import { JournalCarousel } from '~/components/JournalCarousel';
import { contentMeta } from '~/utils/contentMeta';

export const meta = contentMeta;

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export const journalSearchSchema = z.object({
	category: z.string().optional(),
});

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const searchParams = ensuredSearchObject(request, journalSearchSchema);

	const [{ contentPage }, { journalCategories }, { journalArticles }] = await Promise.all([
		runSudoGraphql(GetContentPageDocument, { where: { slug: 'journal' } }),
		runSudoGraphql(GetJournalCategoriesDocument),
		runSudoGraphql(GetJournalArticlesDocument, {
			where: {
				status: { equals: GQLJournalArticleStatusType.Published },
				category: searchParams.category
					? {
							title: { equals: searchParams.category },
					  }
					: undefined,
			},
			order: {},
		}),
	]);

	invariantNotFound(contentPage, 'No content found');
	invariantNotFound(journalCategories, 'No journal articles found');
	invariantNotFound(journalArticles, 'No journal articles found');

	return cachedJsonDay({
		params: searchParams,
		contentPage,
		journalCategories,
		journalArticles,
	});
};

export const journalCard = (article: GQLJournalFragment, key: number) => (
	<Link to={WebRoutes.JournalPage(article.slug)}>
		<div className="flex flex-col border border-blue rounded-3xl overflow-hidden relative h-full" key={key}>
			<Pill
				className={cn('w-fit h-6 capitalize text-xs absolute right-8 top-8')}
				style={{ backgroundColor: article.category.colour || '#59EB96' }}
			>
				{article?.category.title}
			</Pill>
			<img
				loading="lazy"
				src={article.heroBanner?.structure?.[0]?.image?.image.url}
				className="aspect-[5/3] lg:aspect-[4/3] w-full border-b border-blue object-cover"
				alt={article.heroBanner?.structure?.[0]?.image?.altText || 'Article Image'}
			/>
			<div className="flex flex-col p-8 h-full gap-4 xl:aspect-[5/3]">
				<h4 className="font-display font-light text-lg lg:text-xl normal-case">{article?.heroBanner?.structure?.[0]?.title}</h4>
				<CTAText text={article?.heroBanner?.structure?.[0]?.text} />
				<OutlineLinkButton
					to={WebRoutes.JournalPage(article.slug)}
					size={ButtonSize.M}
					icon={SmallArrowIcon}
					iconSize={ButtonSize.S}
					className="w-fit uppercase mt-auto"
				>
					<h5 className={'text-xs'}>Read More</h5>
				</OutlineLinkButton>
			</div>
		</div>
	</Link>
);

export default function () {
	const { contentPage, journalCategories, journalArticles, params } = useLoaderData<typeof loader>();

	return (
		<div className="mb-10 lg:mb-20">
			<h1 className="hidden">{contentPage.headingTag || contentPage.metaTitle}</h1>
			<JournalCarousel content={contentPage} />
			<div className="p-4 lg:p-8 space-y-7 max-w-8xl mx-auto">
				<div className="flex items-center justify-between">
					<p className="text-lg lg:text-xl font-display font-light">Category</p>
					{/* <p className="text-sm lg:text-base inline-flex items-center">
						Sort by <ArrowDownIcon className={'w-auto h-4 ml-2'} />
					</p> */}
				</div>
				<div className="space-y-10 lg:space-y-20">
					<div className="flex flex-col lg:flex-row gap-5 lg:gap-8">
						{/* Categories */}
						<div className="flex flex-row lg:flex-col gap-3 flex-wrap mb-10 lg:mb-0 mr-0 lg:mr-20">
							<NavLink to={`${WebRoutes.JournalListing}`}>
								<Pill className={cn('w-fit h-fit capitalize text-xs whitespace-nowrap bg-blue text-white')}>
									All Articles
								</Pill>
							</NavLink>
							{/* Other Categories */}
							{journalCategories?.map(category => {
								return (
									<NavLink key={category.id} to={`${WebRoutes.JournalListing}?category=${category.title}`}>
										<Pill
											className={cn(
												'w-fit h-fit capitalize text-xs whitespace-nowrap',
												params.category === category.title && 'bg-blue text-white',
											)}
											style={{ backgroundColor: category.colour || '#A8E8EC' }}
										>
											{category.title}
										</Pill>
									</NavLink>
								);
							})}
						</div>

						<ImageSplitCTA
							className="w-full min-h-[381px] lg:min-h-[575px]"
							clickableBlocks
							content={{
								imageSplitCTA: {
									structure: journalArticles?.slice(0, 2).map(article => ({
										image: article.heroBanner?.structure?.[0]?.image,
										title: article.heroBanner?.structure?.[0]?.title,
										text: article.heroBanner?.structure?.[0]?.text,
										links: [
											{
												to: WebRoutes.JournalPage(article.slug),
												text: 'Read More',
											},
										],
									})),
								},
							}}
						/>
					</div>

					{/* Articles */}
					<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
						{journalArticles.slice(2, 8).map((article, _index) => journalCard(article as any as GQLJournalFragment, _index))}
					</div>

					{/* Single CTA */}
					{!!journalArticles[8]?.heroBanner?.structure?.[0] && (
						<RoundedCTA
							cta={{
								structure: journalArticles[8].heroBanner.structure?.[0],
							}}
							pill={
								journalArticles[8].category.colour && journalArticles[8].category.title
									? {
											text: journalArticles[8].category.title,
											colour: journalArticles[8].category.colour,
									  }
									: undefined
							}
						/>
					)}

					{/* Articles */}
					<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
						{journalArticles.slice(9).map((article, _index) => journalCard(article as any as GQLJournalFragment, _index))}
					</div>

					{/* Simple Dual CTA */}
					<SplitCTA content={contentPage} />
				</div>
			</div>
		</div>
	);
}
